/**
 * Waypoint Type Definitions
 *
 * Defines data structures for waypoints in mission planning,
 * following MAVLink protocol specifications for KOPASGAT Drone Planner.
 */

/**
 * MAVLink Frame of Reference
 * Determines how position coordinates are interpreted
 */
export enum WaypointFrame {
  GLOBAL = 0,                    // Global coordinate frame, WGS84 coordinate system
  LOCAL_NED = 1,                 // Local North-East-Down coordinate frame
  MISSION = 2,                   // Not a coordinate frame, indicates a mission command
  GLOBAL_RELATIVE_ALT = 3,       // Global coordinate frame with altitude relative to home
  LOCAL_ENU = 4,                 // Local East-North-Up coordinate frame
  GLOBAL_INT = 5,                // Global coordinate frame, integer lat/lon (MISSION_ITEM_INT)
  GLOBAL_RELATIVE_ALT_INT = 6,   // Global with relative altitude, integer coordinates
  LOCAL_OFFSET_NED = 7,          // Offset in body NED frame
  BODY_NED = 8,                  // Body fixed frame of reference
  BODY_OFFSET_NED = 9,           // Offset in body frame
  GLOBAL_TERRAIN_ALT = 10,       // Global with altitude relative to terrain
  GLOBAL_TERRAIN_ALT_INT = 11,   // Global terrain-relative with integer coordinates
}

/**
 * MAVLink Command Types (subset - full list in mavlink-commands.ts)
 */
export type MavCommand = number;

/**
 * Single waypoint in a mission
 */
export interface Waypoint {
  // Identity
  id: string;                    // Unique identifier (UUID)
  sequence: number;              // Order in mission (0-based index)

  // Position (WGS84 coordinates)
  latitude: number;              // Decimal degrees (-90 to 90)
  longitude: number;             // Decimal degrees (-180 to 180)
  altitude: number;              // Altitude in meters

  // MAVLink Command
  command: MavCommand;           // MAVLink command type (e.g., NAV_WAYPOINT = 16)
  frame: WaypointFrame;          // Coordinate frame for position

  // Command Parameters (MAVLink specific - meaning depends on command type)
  param1: number;                // Command-specific parameter 1
  param2: number;                // Command-specific parameter 2
  param3: number;                // Command-specific parameter 3
  param4: number;                // Command-specific parameter 4

  // Mission Control
  isCurrentWaypoint: boolean;    // Currently active waypoint during mission execution
  autocontinue: boolean;         // Auto-continue to next waypoint (1) or wait for user (0)

  // UI State
  isSelected: boolean;           // Selected in UI for editing
  isHovered: boolean;            // Mouse hover state

  // User Metadata (optional)
  label?: string;                // User-defined waypoint name/label
  notes?: string;                // User notes/comments for this waypoint
  color?: string;                // Custom marker color (hex or color name)

  // Validation State (optional)
  validationErrors?: string[];   // List of validation errors for this waypoint
  validationWarnings?: string[]; // List of validation warnings for this waypoint

  // Metadata
  createdAt: Date;              // Timestamp when waypoint was created
  updatedAt: Date;              // Timestamp when waypoint was last modified
}

/**
 * Waypoint creation input (minimal required fields)
 * Used when creating new waypoints - defaults will be applied
 */
export interface WaypointInput {
  latitude: number;
  longitude: number;
  altitude: number;
  command?: MavCommand;          // Defaults to NAV_WAYPOINT (16)
  frame?: WaypointFrame;         // Defaults to GLOBAL_RELATIVE_ALT_INT (6)
  param1?: number;               // Defaults to 0
  param2?: number;               // Defaults to 3 (acceptance radius)
  param3?: number;               // Defaults to 0
  param4?: number;               // Defaults to 0 (yaw)
  autocontinue?: boolean;        // Defaults to true
  label?: string;                // Optional waypoint label
  notes?: string;                // Optional waypoint notes
  color?: string;                // Optional custom marker color
}

/**
 * Waypoint validation result
 */
export interface WaypointValidation {
  isValid: boolean;              // Overall validation result
  errors: string[];              // Critical errors that prevent usage
  warnings: string[];            // Non-critical warnings for user attention
}

/**
 * Waypoint update payload (partial update)
 */
export type WaypointUpdate = Partial<Omit<Waypoint, 'id' | 'sequence' | 'createdAt'>>;

/**
 * Type guard to check if a value is a valid Waypoint
 */
export function isWaypoint(value: unknown): value is Waypoint {
  if (!value || typeof value !== 'object') return false;

  const wp = value as Waypoint;
  return (
    typeof wp.id === 'string' &&
    typeof wp.sequence === 'number' &&
    typeof wp.latitude === 'number' &&
    typeof wp.longitude === 'number' &&
    typeof wp.altitude === 'number' &&
    typeof wp.command === 'number' &&
    typeof wp.frame === 'number'
  );
}
